import { FC, MouseEvent, useEffect, useRef, useState } from 'react';
import { Button, Tooltip } from '@ibsys/kit';
import { CheckIcon, CopyIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/** Сколько кнопка держит отметку об успешном копировании */
const COPY_FEEDBACK_TIMEOUT = 1500;

type Props = {
  /** Текст, который уходит в буфер обмена */
  text: string;
  /** Подсказка и aria-label кнопки */
  tooltip: string;
  className?: string;
};

/**
 * Кнопка копирования текста в буфер обмена.
 *
 * Клик не всплывает: кнопка живёт внутри строк и карточек со своим переходом, и копирование
 * не должно попутно уводить пользователя со страницы.
 */
export const CopyButton: FC<Props> = ({ text, tooltip, className }) => {
  const { t } = useTranslation('common');
  const [isCopied, setIsCopied] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Очищаем таймер отметки при размонтировании
  useEffect(
    () => () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    },
    [],
  );

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    navigator.clipboard.writeText(text);
    setIsCopied(true);
    if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    copyTimeoutRef.current = setTimeout(() => setIsCopied(false), COPY_FEEDBACK_TIMEOUT);
  };

  const label = isCopied ? t('copied') : tooltip;

  return (
    <Tooltip tooltip={label}>
      <Button variant="ghost" size="icon" className={className ?? 'size-6'} aria-label={label} onClick={handleClick}>
        {isCopied ? <CheckIcon className="size-3.5" /> : <CopyIcon className="size-3.5" />}
      </Button>
    </Tooltip>
  );
};
